import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import moment from "moment";
import { API_POST, DBRequest, useConfig } from "../../../api/api";
import ExoDialog from "../../../components/exo/ExoDialog";
import ExoForm, { FormControls } from "../../../components/exo/ExoForm";
import { ExoFilePreview } from "../../../components/exo/ExoFilePreview";
import Loader from "../../../components/special/Loader";
import SuccessCheck from "../../../components/special/SuccessCheck";
import FailureCross from "../../../components/special/FailureCross";
import { renderHours } from "../../time-tracking/worktime";
import { tokens } from "../../../global/theme/tokens";

const SendTimesheetsMail = ({ open, onClose, monthKey, items = [] }) => {
  const config = useConfig();
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const [selectedIds, setSelectedIds] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successfull, setSuccessfull] = useState(null);
  const [message, setMessage] = useState("");
  const [formData, setFormData] = useState({});
  const [formValid, setFormValid] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSuccessfull(null);
    setSelectedIds(items.filter((item) => item.file).map((item) => item.id));
  }, [open, monthKey]);

  const startDataCollection = useMemo(() => {
    if (!config) return null;
    const period = moment(monthKey, "YYYY-MM").format("MMMM YYYY");
    return {
      email: config.timesheet?.taxConsultantEmail || "",
      subject: t("Timesheets {{company}} {{period}}", {
        company: config.owncompanydata?.companyName || "",
        period,
      }).replace(/\s+/g, " "),
      mailContent: config.timesheet?.taxConsultantMailText || "",
    };
  }, [config, monthKey]);

  function toggleSelected(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }

  function handleSubmit() {
    DBRequest({
      config,
      path: "time-sheets/send-email",
      method: API_POST({
        email: formData.email,
        subject: formData.subject,
        mailContent: formData.mailContent || null,
        timeSheetIds: selectedIds,
      }),
      onLoading: setIsLoading,
      onResponse: (res) => {
        setSuccessfull(true);
        setMessage(res.message);
      },
      onError: (res) => {
        setSuccessfull(false);
        setMessage(res.message);
      },
    });
  }

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("Invalid email format"))
      .required(t("Email address is required")),
    subject: Yup.string().required(t("Subject is required")),
  });

  return (
    <ExoDialog open={open} onClose={onClose} limitWidth maxWidth="1200px">
      <Loader active={isLoading} />
      {successfull !== null ? (
        <Box className="flex h-full flex-col">
          <Box className="flex h-full justify-center items-center flex-col">
            {successfull ? <SuccessCheck /> : <FailureCross />}
            <Typography variant="h4" textAlign="center">
              {successfull
                ? t("Email successfully sent")
                : t("Email could not be sent")}
            </Typography>
          </Box>
          <Box className="flex flex-row-reverse justify-between items-center">
            {!successfull && (
              <Button
                onClick={() => setSuccessfull(null)}
                variant="contained"
                color={"warning"}
              >
                {t("Retry")}
              </Button>
            )}
            <Button onClick={onClose} color={"error"} variant="outlined">
              {t("Close")}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box className="flex flex-col gap-4 h-full overflow-hidden">
          <Typography variant="h4">{t("Send to Tax Consultant")}</Typography>
          <Box className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0 overflow-hidden">
            <Box className="flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto">
              {startDataCollection && (
                <ExoForm
                  className="flex flex-col gap-4"
                  fields={[
                    {
                      key: "email",
                      type: "text",
                      label: t("Email Address"),
                    },
                    {
                      key: "subject",
                      type: "text",
                      label: t("Subject"),
                    },
                    {
                      key: "mailContent",
                      type: "richText",
                      label: `${t("Additional Mail Content")} (${t(
                        "optional"
                      )})`,
                    },
                  ]}
                  startDataCollection={startDataCollection}
                  noDifferences
                  noControls
                  onChange={(data, valid) => {
                    setFormData(data);
                    setFormValid(valid);
                  }}
                  validationSchema={validationSchema}
                />
              )}
            </Box>
            <Box className="flex flex-col gap-1 flex-1 min-h-0 overflow-hidden">
              <Typography variant="h6">{t("Attached Timesheets")}</Typography>
              <Box className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto">
                {items.map((item) => (
                <Box
                  key={item.id}
                  className="flex flex-row items-center justify-between px-2 py-1 rounded-lg"
                  sx={{
                    opacity: item.file ? 1 : 0.5,
                    "&:hover": {
                      backgroundColor: colors.bgInput,
                    },
                  }}
                >
                  <Box className="flex flex-row items-center gap-2">
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(item.id)}
                      disabled={!item.file}
                      onChange={() => toggleSelected(item.id)}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.user?.lastName} {item.user?.firstName}
                    </Typography>
                  </Box>
                  <Box className="flex flex-row items-center gap-2">
                    <Typography
                      variant="body2"
                      className="monospace"
                      sx={{ opacity: 0.7 }}
                    >
                      {renderHours(item.minutesTracked)} h
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setPreviewFile(item.file)}
                      disabled={!item.file}
                      title={t("Preview")}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <FormControls
            onSubmit={handleSubmit}
            onCancel={onClose}
            isValid={formValid && selectedIds.length > 0}
            submitText={t("Send Email")}
            handleReset={() => {}}
          />
        </Box>
      )}
      <ExoFilePreview
        open={Boolean(previewFile)}
        src={previewFile?.temporaryUrl}
        mimeType={previewFile?.mimeType || "application/pdf"}
        onClose={() => setPreviewFile(null)}
      />
    </ExoDialog>
  );
};

export default SendTimesheetsMail;
